import { Button, ChoiceChip, Text, Textarea } from '@components/ui';
import { useCompleteJobMutation } from '@lib/api/jobs';
import { useRateBuyerMutation } from '@lib/api/rating';
import { logError } from '@lib/logError';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type { RatingLocationState } from './r8trRatingTypes';


const ratingChipSx: SxProps<Theme> = {
  ml: '10px',
  mt: '10px',
  borderRadius: '15px',
  px: '25px',
  py: '10px',
  fontSize: 18,
};

const R8trRating = (): JSX.Element => {
  const { t } = useTranslation();
  const rateBuyer = useRateBuyerMutation();
  const completeJob = useCompleteJobMutation();
  const navigate = useNavigate();
  const TabsArray = [8, 7, 6, 5, 4, 3, 2, 1];
  const location = useLocation();
  const [tabsValue, setTabsValue] = useState(8);
  const [buyerRating, setBuyerRating] = useState(8);
  const jobData = (location.state as RatingLocationState).jobData;
  const [loading, setLoading] = useState(false);
  const [commentOne, setCommentOne] = useState('');
  const [commentTwo, setCommentTwo] = useState('');
  const handler8tingOptionTab = (value: number): void => {
    TabsArray.map((item) => {
      if (value == item) {
        setTabsValue(item);
      }
    });
  };
  const handleBuyerR8tingTab = (value: number): void => {
    TabsArray.map((item) => {
      if (value == item) {
        setBuyerRating(item);
      }
    });
  };

  const handleUploadReview = (): void => {
    setLoading(true);
    rateBuyer
      .mutateAsync({
        rating: buyerRating ? buyerRating : 0,
        ratingType: 'buyer',
        appRating: tabsValue ? tabsValue : 0,
        betterAsACompanyComment: commentOne,
        improvementComment: commentTwo,
        jobId: jobData.id,
      })
      .then(() => completeJob.mutateAsync(jobData.id))
      .then(() => {
        setLoading(false);
        toast.success('Ratings Submitted Successfully');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      })
      .catch((error: unknown) => {
        setLoading(false);
        logError('rate buyer', error);
      });
  };

  const handleCommentOne = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setCommentOne(event.target.value);
  };

  const handleCommentTwo = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setCommentTwo(event.target.value);
  };

  return (
    <Box
      sx={{
        mt: '30px',
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: { xs: '83.333%', md: '66.666%' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '60%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700 }}>{t('rating.youJustEarned')}</Text>
          <Text tone="primary" sx={{ fontSize: 50, fontWeight: 700 }}>
            ${jobData.buyerCost / 2}
          </Text>
          <Text sx={{ fontSize: 16 }}>
            {t('rating.betaPayout')}
          </Text>
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Text
            sx={{ fontSize: 18, fontWeight: 700 }}
          >
            {t('rating.rateBuyer')}
          </Text>
          <Box
            role="group"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              p: 0,
              m: 0,
            }}
          >
            {TabsArray.length > 0 &&
              TabsArray.map((item) => (
                <ChoiceChip
                  key={item}
                  selected={buyerRating == item}
                  onClick={() => {
                    handleBuyerR8tingTab(item);
                  }}
                  sx={ratingChipSx}
                >
                  {item}
                </ChoiceChip>
              ))}
          </Box>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Text
            sx={{ fontSize: 18, fontWeight: 700 }}
          >
            {t('rating.rateApp')}
          </Text>
          <Box
            role="group"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              p: 0,
              m: 0,
            }}
          >
            {TabsArray.length > 0 &&
              TabsArray.map((item) => (
                <ChoiceChip
                  key={item}
                  selected={tabsValue == item}
                  onClick={() => {
                    handler8tingOptionTab(item);
                  }}
                  sx={ratingChipSx}
                >
                  {item}
                </ChoiceChip>
              ))}
          </Box>
        </Box>
      </Box>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        sx={{ mt: '30px', width: '100%' }}
      >
        <Grid item xs={12} md={4}>
          <Text sx={{ fontSize: 16, fontWeight: 700 }}>
            {t('rating.doBetter')}
          </Text>
          <Textarea
            placeholder={t('common.messagePlaceholder')}
            value={commentOne}
            minRows={4}
            onChange={handleCommentOne}
            sx={{ '& textarea': { resize: 'none' } }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Text sx={{ fontSize: 16, fontWeight: 700 }}>
            {t('rating.suggestions')}
          </Text>
          <Textarea
            placeholder={t('common.messagePlaceholder')}
            value={commentTwo}
            minRows={4}
            onChange={handleCommentTwo}
            sx={{ '& textarea': { resize: 'none' } }}
          />
        </Grid>
      </Grid>

      <Button
        variant="primary"
        onClick={handleUploadReview}
        sx={{
          height: 45,
          width: '20%',
          mt: 2,
          borderRadius: '0px 15px 0px 15px',
          color: 'common.white',
        }}
        loading={loading}
      >
        {t('common.submit')}
      </Button>
    </Box>
  );
};

export default R8trRating;
